import { Upload, Download, FileUp } from "lucide-react";
import { useCsvImport } from "../hooks/useCsvImport";
import { parseCsvRows, downloadCsvTemplate } from "../lib/csvImport";
import { FormDialog, Field, ActionButton } from "./common";

const HEADER_MAP = {
  name: "name",
  full_name: "name",
  email: "email",
  role: "role",
  active: "active",
  hourly_rate: "hourly_rate",
  rate: "hourly_rate",
  password_hash: "password_hash",
  password: "password",
};

const TEMPLATE = "name,email,role,hourly_rate,password\nSample Mechanic,mechanic@example.com,mechanic,28,ChangeMe-Example\n";

const parseUsers = (raw) => parseCsvRows(raw, HEADER_MAP, "email");

export const UserImport = ({ open, onOpenChange, onDone }) => {
  const { rows, parseErrors, csvText, busy, fileRef, reset, onFile, onPaste, submit } = useCsvImport({
    parse: parseUsers, endpoint: "/users/import", entity: "users", onOpenChange, onDone,
  });
  const downloadTemplate = () => downloadCsvTemplate(TEMPLATE, "users_import_template.csv");

  return (
    <FormDialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }} title="Import Users" testId="user-import-dialog">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400 leading-relaxed max-w-[320px]">
          CSV columns: <span className="font-mono text-amber-400/90">name, email, role, hourly_rate, password_hash</span> (or <span className="font-mono text-amber-400/90">password</span>). The exported Users CSV imports as-is — <span className="text-emerald-400">password hashes are preserved</span> so mechanics keep their logins. Existing emails are skipped.
        </p>
        <button type="button" data-testid="user-import-template-download" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-sky-400 hover:text-sky-300 transition-colors duration-200">
          <Download size={13} /> Template
        </button>
      </div>

      <Field label="Upload CSV file">
        <label data-testid="user-import-drop" className="flex items-center justify-center gap-2 border border-dashed border-slate-600 rounded-md px-4 py-5 text-sm text-slate-400 hover:border-amber-500/50 hover:text-amber-300 cursor-pointer transition-colors duration-200">
          <FileUp size={16} /> Choose .csv file
          <input ref={fileRef} data-testid="user-import-file" type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        </label>
      </Field>

      <Field label="…or paste CSV text">
        <textarea data-testid="user-import-textarea" value={csvText} onChange={(e) => onPaste(e.target.value)} rows={5} className="w-full rounded-md bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-slate-100 font-mono focus:border-amber-500/60 focus:outline-none" placeholder="name,email,role,hourly_rate,password_hash" />
      </Field>

      {parseErrors.length > 0 && <p className="text-xs text-rose-400" data-testid="user-import-errors">{parseErrors.length} row(s) could not be read. Required column: email.</p>}
      {rows.length > 0 && <p className="text-xs text-emerald-400" data-testid="user-import-count">{rows.length} user(s) ready to import.</p>}

      <ActionButton testId="user-import-submit" onClick={submit} disabled={busy || rows.length === 0} className="w-full justify-center">
        <Upload size={16} /> {busy ? "Importing…" : `Import ${rows.length || ""} Users`}
      </ActionButton>
    </FormDialog>
  );
};
